// SD61 / FR-AD7: the per-recording debug view. Paste a recording id (or any link containing
// one) and see everything Grackle knows about it: the row, its storage, renditions, transcript,
// live session / notetaker bot, and every job that names it. Downloads and republish need a
// written reason and go to the ops audit log (server/src/ops_grackles.rs).
import { useCallback, useEffect, useRef, useState, type FormEvent, type ReactNode } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, ApiFail } from "../api/client";
import { Field, Notice } from "../components/ui";
import "../admin/admin.css";
import { Badge, Button, Card } from "@grackle/ui";

type Row = Record<string, unknown>;

export interface OpsGrackleDebug {
  grackle: Row & { id: string; title: string; status: string };
  org: { id: string; name: string; slug: string };
  owner: { id: string; email: string; name: string | null };
  renditions: Row[];
  storage: {
    prefix: string;
    objects: { key: string; bytes: number; last_modified: string; role: string | null }[];
    object_count: number;
    total_bytes: number;
    truncated: boolean;
    missing: { key: string; role: string }[];
    error: string | null;
  };
  transcript: Row | null;
  live_sessions: Row[];
  bots: Row[];
  jobs: (Row & { id: string; kind: string; status: string; payload: Row })[];
  share_links: Row[];
  ops_audit: Row[];
  can_republish: boolean;
}

const UUID_RE = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;

/** The recording id in whatever was pasted: a bare id, a `/g/<id>` link, an ops URL. */
export function extractGrackleId(text: string): string | null {
  return text.match(UUID_RE)?.[0].toLowerCase() ?? null;
}

export function formatBytes(n: number | null | undefined): string {
  if (n == null) return "—";
  if (n < 1024) return `${n} B`;
  const units = ["KB", "MB", "GB", "TB"];
  let v = n / 1024;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  return `${v.toFixed(v >= 100 ? 0 : 1)} ${units[i]}`;
}

function clock(v: unknown): string {
  if (typeof v !== "string" || !v) return "—";
  return new Date(v).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
  });
}

function show(v: unknown): string {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}

function duration(ms: unknown): string {
  if (typeof ms !== "number") return "—";
  const s = Math.round(ms / 1000);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return h ? `${h}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}` : `${m}:${String(sec).padStart(2, "0")}`;
}

function statusTone(status: string): "success" | "accent" | "now" | "outline" {
  if (status === "ready" || status === "done") return "success";
  if (status === "processing" || status === "uploading" || status === "running" || status === "queued") return "accent";
  if (status === "failed" || status === "dead") return "now";
  return "outline";
}

function Facts({ items }: { items: [string, ReactNode][] }) {
  return (
    <dl className="ops-facts">
      {items.map(([k, v]) => (
        <div key={k} className="ops-facts__row">
          <dt className="gk-overline">{k}</dt>
          <dd>{v}</dd>
        </div>
      ))}
    </dl>
  );
}

function Lookup({ initial }: { initial?: string }) {
  const navigate = useNavigate();
  const [text, setText] = useState(initial ?? "");
  const [error, setError] = useState<string | null>(null);
  function onSubmit(e: FormEvent) {
    e.preventDefault();
    const id = extractGrackleId(text);
    if (!id) {
      setError("Paste a recording id or a link that contains one.");
      return;
    }
    setError(null);
    navigate(`/ops/videos/${id}`);
  }
  return (
    <form className="ops-lookup" onSubmit={onSubmit}>
      <Field
        label="Recording id or link"
        className="mono"
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="0199… or https://…/g/0199…"
        error={error ?? undefined}
      />
      <Button type="submit" icon="magnifying-glass">
        Look up
      </Button>
    </form>
  );
}

export default function OpsVideosPage() {
  const { grackleId } = useParams();
  const [data, setData] = useState<OpsGrackleDebug | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(() => {
    if (!grackleId) return;
    setNotFound(false);
    api<OpsGrackleDebug>("GET", `/ops/grackles/${grackleId}`)
      .then((res) => {
        setData(res);
        setError(null);
      })
      .catch((err) => {
        if (err instanceof ApiFail && err.status === 404) setNotFound(true);
        else setError(err instanceof ApiFail ? err.message : "Couldn't load that recording.");
      });
  }, [grackleId]);

  useEffect(() => {
    setData(null);
    load();
  }, [load]);

  const reasonOk = reason.trim().length >= 5;
  const [reasonError, setReasonError] = useState<string | null>(null);
  const reasonRef = useRef<HTMLDivElement>(null);
  /** Downloads and republish need a reason; say so where it's typed instead of greying out. */
  function needReason(): boolean {
    if (reasonOk) return false;
    setReasonError("Add a reason first (a few words). It's recorded in the ops audit log.");
    reasonRef.current?.scrollIntoView({ block: "center" });
    reasonRef.current?.querySelector("input")?.focus();
    return true;
  }

  async function onDownload(key: string) {
    if (!grackleId || needReason()) return;
    setBusy(key);
    setError(null);
    try {
      const res = await api<{ url: string }>("POST", `/ops/grackles/${grackleId}/download`, { key, reason });
      window.open(res.url, "_blank", "noopener");
      load();
    } catch (err) {
      setError(err instanceof ApiFail ? err.message : "Couldn't sign that download.");
    } finally {
      setBusy(null);
    }
  }

  async function onRepublish() {
    if (!grackleId || needReason()) return;
    setBusy("republish");
    setError(null);
    try {
      await api("POST", `/ops/grackles/${grackleId}/republish`, { reason });
      setNotice("Publish queued. This page refreshes with the new job.");
      load();
    } catch (err) {
      setError(err instanceof ApiFail ? err.message : "Couldn't queue the publish.");
    } finally {
      setBusy(null);
    }
  }

  async function onRetranscribe() {
    if (!grackleId || needReason()) return;
    setBusy("retranscribe");
    setError(null);
    try {
      await api("POST", `/ops/grackles/${grackleId}/retranscribe`, { reason });
      setNotice("Transcription queued. This page refreshes with the new job.");
      load();
    } catch (err) {
      setError(err instanceof ApiFail ? err.message : "Couldn't queue the transcription.");
    } finally {
      setBusy(null);
    }
  }

  async function onRetryJob(jobId: string) {
    setBusy(jobId);
    setError(null);
    try {
      await api("POST", `/ops/jobs/${jobId}/retry`);
      load();
    } catch (err) {
      setError(err instanceof ApiFail ? err.message : "Couldn't retry that job.");
    } finally {
      setBusy(null);
    }
  }

  const head = (
    <header className="page-head">
      <div className="page-head__grow">
        <h1>Videos</h1>
        <p>Everything about one recording: its row, storage, renditions and the jobs working on it.</p>
      </div>
    </header>
  );

  if (!grackleId) {
    return (
      <>
        {head}
        <Card>
          <Lookup />
        </Card>
      </>
    );
  }

  const g = data?.grackle;
  const prefix = data?.storage.prefix ?? "";

  return (
    <>
      {head}
      <Card>
        <Lookup key={grackleId} initial={grackleId} />
      </Card>
      {error ? <Notice tone="error">{error}</Notice> : null}
      {notice ? <Notice>{notice}</Notice> : null}
      {notFound ? <Notice tone="error">No recording has that id.</Notice> : null}
      {!data || !g ? (
        notFound ? null : <div className="gk-sm">Loading…</div>
      ) : (
        <>
          <Card
            title={g.title}
            action={
              <Badge tone={statusTone(g.status)} size="sm">
                {g.status}
              </Badge>
            }
          >
            {g.error ? <Notice tone="error">{show(g.error)}</Notice> : null}
            <Facts
              items={[
                ["Id", <span className="mono">{g.id}</span>],
                ["Org", <Link to={`/ops/orgs/${data.org.id}`}>{data.org.name}</Link>],
                ["Owner", <span>{data.owner.name ? `${data.owner.name} · ` : ""}{data.owner.email}</span>],
                ["Created", clock(g.created_at)],
                ["Duration", <span className="gk-num">{duration(g.duration_ms)}</span>],
                ["Size", <span className="gk-num">{formatBytes(g.source_bytes as number | null)}</span>],
                ["Resolution", g.source_width ? `${show(g.source_width)}×${show(g.source_height)}` : "—"],
                ["Visibility", show(g.visibility)],
                ["Upload id", <span className="mono">{show(g.upload_id)}</span>],
                ["Remux", `${show(g.remux_state)}${g.remux_error ? ` — ${show(g.remux_error)}` : ""}`],
                ["Transcript", `${show(g.transcript_status)}${g.transcript_error ? ` — ${show(g.transcript_error)}` : ""}`],
                ["Diarization", show(g.diarization_status)],
                ["Meeting source", show(g.meeting_source)],
                ["Trashed", clock(g.trashed_at)],
                ["Deleted", clock(g.deleted_at)],
                ["Purged", clock(g.purged_at)],
                ["Legal hold", show(g.legal_hold)],
              ]}
            />
            <div className="ops-actions">
              <div ref={reasonRef}>
                <Field
                  label="Reason for access"
                  value={reason}
                  onChange={(e) => {
                    setReason(e.target.value);
                    setReasonError(null);
                  }}
                  help="Required to download, republish or re-transcribe. Recorded in the ops audit log."
                  error={reasonError ?? undefined}
                />
              </div>
              <div className="ops-actions__buttons">
                <Button variant="secondary" size="sm" icon="arrow-clockwise" disabled={!data.can_republish || busy !== null} onClick={onRepublish}>
                  {busy === "republish" ? "Queuing…" : "Republish"}
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  icon="subtitles"
                  disabled={g.status !== "ready" || busy !== null}
                  onClick={onRetranscribe}
                >
                  {busy === "retranscribe" ? "Queuing…" : "Re-transcribe"}
                </Button>
                <Button variant="ghost" size="sm" icon="arrow-square-out" onClick={() => window.open(`/g/${g.id}`, "_blank", "noopener")}>
                  Open in app
                </Button>
                <Button variant="ghost" size="sm" icon="arrows-clockwise" onClick={load}>
                  Refresh
                </Button>
              </div>
            </div>
          </Card>

          <Card size="flush" className="admin-table-card" title="Storage">
            <div className="ops-card-pad">
              <div className="gk-sm">
                <span className="mono">{prefix}</span> ·{" "}
                <span className="gk-num">
                  {data.storage.object_count} objects, {formatBytes(data.storage.total_bytes)}
                </span>
                {data.storage.truncated ? " (listing truncated)" : ""}
              </div>
              {data.storage.error ? <Notice tone="error">Couldn't list storage: {data.storage.error}</Notice> : null}
              {data.storage.missing.length ? (
                <Notice tone="error">
                  The row points at objects that aren't in the bucket:{" "}
                  {data.storage.missing.map((m) => `${m.role} (${m.key.replace(prefix, "")})`).join(", ")}
                </Notice>
              ) : null}
            </div>
            <table className="admin-table">
              <thead>
                <tr>
                  <th className="gk-overline">Key</th>
                  <th className="gk-overline">What</th>
                  <th className="gk-overline num">Size</th>
                  <th className="gk-overline">Modified</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {data.storage.objects.map((o) => (
                  <tr key={o.key}>
                    <td className="mono ops-key">{o.key.replace(prefix, "")}</td>
                    <td>{o.role ?? "—"}</td>
                    <td className="num">{formatBytes(o.bytes)}</td>
                    <td className="mono">{clock(o.last_modified)}</td>
                    <td>
                      <Button variant="secondary" size="sm" icon="download-simple" disabled={busy !== null} onClick={() => onDownload(o.key)}>
                        {busy === o.key ? "Signing…" : "Download"}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>

          <Card size="flush" className="admin-table-card" title="Jobs">
            {data.jobs.length === 0 ? (
              <div className="ops-card-pad gk-sm">No job has named this recording.</div>
            ) : (
              <table className="admin-table">
                <thead>
                  <tr>
                    <th className="gk-overline">Kind</th>
                    <th className="gk-overline">Status</th>
                    <th className="gk-overline num">Attempts</th>
                    <th className="gk-overline">Created · claimed · heartbeat</th>
                    <th className="gk-overline">Last error</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {data.jobs.map((j) => (
                    <tr key={j.id}>
                      <td>
                        <div className="admin-row-name mono">
                          {j.kind}
                          {typeof j.payload.mode === "string" ? ` · ${j.payload.mode}` : ""}
                        </div>
                        <div className="admin-row-sub mono">{j.id}</div>
                      </td>
                      <td>
                        <Badge tone={statusTone(j.status)} size="sm">
                          {j.status}
                        </Badge>
                      </td>
                      <td className="num">
                        {show(j.attempts)}/{show(j.max_attempts)}
                      </td>
                      <td className="mono">
                        <div>{clock(j.created_at)}</div>
                        <div>{clock(j.claimed_at)}</div>
                        <div>{clock(j.heartbeat_at)}</div>
                      </td>
                      <td className="ops-error">{show(j.last_error)}</td>
                      <td>
                        {j.status === "failed" || j.status === "dead" ? (
                          <Button variant="secondary" size="sm" disabled={busy !== null} onClick={() => onRetryJob(j.id)}>
                            {busy === j.id ? "Retrying…" : "Retry"}
                          </Button>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>

          <RowsCard title="Renditions" rows={data.renditions} empty="No renditions: the recording plays from its master." />
          <RowsCard title="Transcript" rows={data.transcript ? [data.transcript] : []} empty="No stored transcript." />
          <RowsCard title="Live sessions" rows={data.live_sessions} empty="No live transcript session." />
          <RowsCard title="Notetaker bots" rows={data.bots} empty="No notetaker bot recorded this." />
          <RowsCard title="Share links" rows={data.share_links} empty="No share links." />
          <RowsCard title="Ops audit" rows={data.ops_audit} empty="No operator activity on this recording." />
        </>
      )}
    </>
  );
}

/** A plain table of whatever columns the rows carry — for the secondary sections, where the
 * server's field names are the clearest labels an operator could ask for. */
function RowsCard({ title, rows, empty }: { title: string; rows: Row[]; empty: string }) {
  const cols = rows.length ? Object.keys(rows[0]) : [];
  return (
    <Card size="flush" className="admin-table-card" title={title}>
      {rows.length === 0 ? (
        <div className="ops-card-pad gk-sm">{empty}</div>
      ) : (
        <div className="ops-scroll">
          <table className="admin-table">
            <thead>
              <tr>
                {cols.map((c) => (
                  <th key={c} className="gk-overline">
                    {c.replace(/_/g, " ")}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i}>
                  {cols.map((c) => (
                    <td key={c} className={c.endsWith("_at") || c.endsWith("id") || c.endsWith("key") ? "mono" : undefined}>
                      {c.endsWith("_at") ? clock(r[c]) : show(r[c])}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
